import { ModalAction, ModalTitle } from '@overlayqa/shared/ui';
import { useEffect, useRef, useState } from 'react';
import { useModalA11y } from '@/hooks/useModalA11y';
import { Folder01Icon } from '@hugeicons/core-free-icons';
import { toast } from 'sonner';
import { useCreateProject } from '../../hooks/useProjects';
import { FigmaUrlInput } from '../../components/figma/FigmaUrlInput';
import { deriveProjectSlug } from '../../lib/projectSlug';
import { linkedFrameNodeId } from '@overlayqa/shared';
import type { ParsedFigmaUrl } from '@overlayqa/shared';
import { log } from '@/lib/logger';
import { ApiError } from '../../lib/api/apiError';
import { isHandledHttpStatus } from '../../lib/api/errorLogLevel';
import { capture } from '@/lib/analytics';
import { ModalPortal } from '@/components/shared/ModalPortal';

interface ProjectsCreateModalProps {
  activeTeamId: string;
  onClose: () => void;
  /** Hands the new project's id back to a caller that keeps the user in place
   *  (FramerReviewPage selects it so the Framer handoff is not lost). */
  onCreated?: (projectId: string) => void;
}

// No plan-runway UI here on purpose (deleted 2026-08-04, user_decisions_log):
// every plan that can create projects is unlimited, so a project-cap line
// could only render for expired-trial users, whom it misled. Server-side
// checkProjectPlanLimit remains the backstop if a finite cap ever returns.
export function ProjectsCreateModal({ activeTeamId, onClose, onCreated }: ProjectsCreateModalProps) {
  const createProject = useCreateProject();
  const isOpen = useRef(true);
  useEffect(() => {
    isOpen.current = true;
    return () => { isOpen.current = false; };
  }, []);
  const dialogRef = useRef<HTMLDivElement>(null);
  useModalA11y(true, dialogRef, () => handleCancel());
  const [newProjectName, setNewProjectName] = useState('');
  const [projectFigmaUrl, setProjectFigmaUrl] = useState('');
  const [parsedFigmaUrl, setParsedFigmaUrl] = useState<ParsedFigmaUrl | null>(null);

  const handleCreateProject = async () => {
    if (!newProjectName.trim()) return;

    // Clamped to the server's slug cap with a non-empty fallback — an
    // unclamped derivation 400'd long and CJK/emoji names (see projectSlug.ts).
    const slug = deriveProjectSlug(newProjectName);

    try {
      const created = await createProject.mutateAsync({
        teamId: activeTeamId,
        name: newProjectName,
        slug,
        figmaFileKey: parsedFigmaUrl?.isValid ? parsedFigmaUrl.fileKey : undefined,
        // A pasted FRAME link names the frame the user wants; both pickers
        // open on it. A file link (no id, or the canvas id a copied file URL
        // carries) persists none (2026-09-10 P0).
        figmaNodeId: parsedFigmaUrl?.isValid ? linkedFrameNodeId(parsedFigmaUrl.nodeId) ?? undefined : undefined,
      });
      // Multi-project creation is an expansion signal; the MCP path already
      // tracks mcp_project_created, onboarding via dashboard_onboarding_completed.
      // project_id makes a create→bulk-create misfile detectable in analytics
      // (2026-08-02: unprovable from PostHog because no event carried one).
      capture(
        'project_created',
        created?.id ? { source: 'modal', project_id: created.id } : { source: 'modal' },
      );
      // ProjectsPage mounts a new instance on reopen. A closed form's reply
      // must not close that replacement (ProjectsPage.tsx, showCreateModal).
      if (isOpen.current) {
        if (created?.id) onCreated?.(created.id);
        onClose();
      }
    } catch (err) {
      // The API client opens the upgrade modal before throwing ApiError
      // (lib/api/client.ts:245-254). Keep that handled refusal out of
      // console.error, which PostHog captures as an application exception.
      if (err instanceof ApiError && isHandledHttpStatus(err.status)) {
        log.warn('Project create refused:', { err });
      } else {
        log.error('Failed to create project:', { err });
      }
      if (isOpen.current) toast.error(err instanceof Error && err.message ? err.message : 'Failed to create project. Please try again.');
    }
  };

  const handleFigmaSave = async (_parsed: ParsedFigmaUrl) => {
    return Promise.resolve();
  };

  const handleCancel = () => {
    isOpen.current = false;
    setNewProjectName('');
    setProjectFigmaUrl('');
    setParsedFigmaUrl(null);
    onClose();
  };

  const overlay = (
    <div
      ref={dialogRef}
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
      role="dialog"
      aria-modal="true"
      aria-labelledby="create-project-title"
      onClick={(e) => { if (e.target === e.currentTarget) handleCancel(); }}
    >
      <div className="bg-white rounded-xl w-full max-w-md shadow-xl overflow-hidden">
        {/* Header */}
        <div className="px-6 py-2 border-b border-gray-100 flex items-center justify-between">
          <ModalTitle icon={Folder01Icon} id="create-project-title" className="oq-dialog__title">Create new project</ModalTitle>
          <button
            type="button"
            onClick={handleCancel}
            className="text-gray-400 hover:text-gray-600 transition-colors p-2"
            aria-label="Close"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form
          id="create-project-form"
          className="px-6 pt-4 pb-5"
          // The optional Figma field is <input type="url">. Without noValidate
          // an unparseable paste there makes the browser refuse to submit the
          // WHOLE form, with its own bubble, so Create reads as broken while
          // the name is fine. A bad paste is simply not linked.
          noValidate
          onSubmit={(e) => { e.preventDefault(); handleCreateProject(); }}
        >
        <fieldset className="mb-5">
          <label htmlFor="projectName" className="field-label">
            Project Name
          </label>
          <input
            id="projectName"
            type="text"
            value={newProjectName}
            onChange={(e) => setNewProjectName(e.target.value)}
            placeholder="My Project"
            className="input input-lg w-full"
            // Mirrors createProjectSchema.name max(100) — packages/server/src/routes/projects.ts
            maxLength={100}
            autoFocus
          />
        </fieldset>

        {/* Optional Figma File Linking */}
        <fieldset className="mb-5">
          <label htmlFor="figma-url" className="field-label">
            Figma File <span className="text-gray-400 font-normal">(optional)</span>
          </label>
          <FigmaUrlInput
            value={projectFigmaUrl}
            onChange={(url, parsed) => {
              setProjectFigmaUrl(url);
              setParsedFigmaUrl(parsed);
            }}
            onSave={handleFigmaSave}
            context="project"
            className="mb-0"
            showSaveButton={false}
            showLabel={false}
            size="lg"
          />
        </fieldset>

        <div className="flex gap-3 justify-end">
          <button
            type="button"
            className="btn-secondary"
            onClick={handleCancel}
          ><ModalAction kind="cancel">
            Cancel
          </ModalAction></button>
          <button
            type="submit"
            className="btn-primary"
            disabled={!newProjectName.trim() || createProject.isPending}
          ><ModalAction kind="add">
            {createProject.isPending ? 'Creating...' : 'Create project'}
          </ModalAction></button>
        </div>
        </form>
      </div>
    </div>
  );

  return <ModalPortal>{overlay}</ModalPortal>;
}
